import { useDeleteNews, useGetAllNews } from "@/api/cms";
import { NewsItemDrawer } from "@/components/cms/NewsItemDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { NewsItem } from "@/lib/cms/cms-types";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { ArrowUpDown, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { cmsNew, useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

/**
 * Section « Actualités » de l'interface WordPress-like du CMS avancé
 * (`/cms/advanced/news`) : gestion centralisée des articles (News) — la même
 * donnée que celle affichée par le bloc CMS avancé `News` et la page News.
 *  - liste en table filtrable : vignette / titre / date / statut / actions ;
 *  - recherche par titre, filtre par statut, tri par date ;
 *  - statut brouillon/publié PAR article (badge) : le site public n'affiche que
 *    les articles publiés (filtre côté serveur) ;
 *  - création / édition via `NewsForm` (drawer), suppression avec confirmation.
 */

type StatusFilter = "ALL" | "PUBLISHED" | "DRAFT";

function newsTitle(item: NewsItem, lang: SupportedLocale): string {
  const title = (item.title ?? {}) as Record<string, string>;
  return (
    title[lang] ||
    Object.values(title).find(Boolean) ||
    cmsNew("cms.new.news.noTitle", "(Sans titre)")
  );
}

function StatusBadge({ status }: { status?: string }) {
  return status === "PUBLISHED" ? (
    <Badge variant="published">{cmsNew("cms.new.auto.text.publie", "Publié")}</Badge>
  ) : (
    <Badge variant="draft">{cmsNew("cms.new.auto.text.brouillon", "Brouillon")}</Badge>
  );
}

export default function CMSAdvancedNews() {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const mainLang = getMainLanguage() as SupportedLocale;

  const { data: news = [], isLoading, refetch } = useGetAllNews();
  const deleteNews = useDeleteNews();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [sortDesc, setSortDesc] = useState(true);
  const [deleteFor, setDeleteFor] = useState<NewsItem | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (news as NewsItem[])
      .filter((item) =>
        statusFilter === "ALL" ? true : item.status === statusFilter,
      )
      .filter((item) =>
        q ? newsTitle(item, mainLang).toLowerCase().includes(q) : true,
      );
    return [...list].sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      return sortDesc ? db - da : da - db;
    });
  }, [news, search, statusFilter, sortDesc, mainLang]);

  const confirmDelete = () => {
    if (!deleteFor?.id) return;
    deleteNews.mutate(deleteFor.id, {
      onSuccess: () => {
        setDeleteFor(null);
        toast({ title: cmsNew("cms.new.auto.title.actualite_supprimee", "Actualité supprimée"), variant: "success", duration: 1600 });
      },
      onError: () => {
        setDeleteFor(null);
        toast({ title: cmsNew("cms.new.auto.title.echec_de_la_suppression", "Échec de la suppression"), variant: "error", duration: 4000 });
      },
    });
  };

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.actualites", "Actualités")}
      subtitle={cmsPhrase(cmsNew, "newsSubtitle")}
      actions={
        <NewsItemDrawer
          refetch={refetch}
          trigger={
            <Button size="sm">
              <Plus size={16} className="mr-1.5" />
              {cmsPhrase(cmsNew, "newsAddButton")}
            </Button>
          }
        />
      }
    >
      <div className="px-6 py-4">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative max-w-xs flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={cmsNew("cms.new.auto.placeholder.rechercher_un_titre", "Rechercher un titre…")}
              className="pl-8"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
          >
            <option value="ALL">{cmsNew("cms.new.news.allStatuses", "Tous les statuts")}</option>
            <option value="PUBLISHED">{cmsNew("cms.new.auto.text.publies", "Publiés")}</option>
            <option value="DRAFT">{cmsNew("cms.new.auto.text.brouillons", "Brouillons")}</option>
          </select>
        </div>

        {isLoading ? (
          <p className="py-12 text-center text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-slate-500">
              {search.trim() || statusFilter !== "ALL"
                ? cmsNew("cms.new.news.noFilterMatch", "Aucune actualité ne correspond aux filtres.")
                : cmsNew("cms.new.news.empty", "Aucune actualité pour le moment.")}
            </p>
            {!search.trim() && statusFilter === "ALL" ? (
              <NewsItemDrawer
                refetch={refetch}
                trigger={
                  <Button size="sm">
                    <Plus size={16} className="mr-1.5" />
                    {cmsNew("cms.new.news.addFirst", "Ajouter la première actualité")}
                  </Button>
                }
              />
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">
                    {cmsPhrase(cmsNew, "newsImageColumn")}
                  </TableHead>
                  <TableHead>{cmsNew("cms.new.auto.text.titre", "Titre")}</TableHead>
                  <TableHead className="w-40">
                    <button
                      type="button"
                      onClick={() => setSortDesc((s) => !s)}
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                      title={cmsNew("cms.new.auto.title.trier_par_date", "Trier par date")}
                    >
                      {cmsPhrase(cmsNew, "newsDateColumn")}
                      <ArrowUpDown size={12} />
                    </button>
                  </TableHead>
                  <TableHead className="w-28">{cmsNew("cms.new.auto.text.statut", "Statut")}</TableHead>
                  <TableHead className="w-28 text-right">{cmsNew("cms.new.auto.text.actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => {
                  const thumb = item.attachments?.[0]?.mediumPublicUrl;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex h-12 w-16 items-center justify-center overflow-hidden rounded bg-slate-100">
                          {thumb ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={thumb}
                              alt={newsTitle(item, mainLang)}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[280px] font-medium text-slate-800">
                        <span
                          className="line-clamp-2 uppercase"
                          style={{ textTransform: "uppercase" }}
                        >
                          {newsTitle(item, mainLang)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {item.date
                          ? new Date(item.date).toLocaleDateString("fr-FR", {
                              year: "numeric",
                              month: "short",
                              day: "2-digit",
                            })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <NewsItemDrawer
                            refetch={refetch}
                            newsItem={item}
                            trigger={
                              <button
                                type="button"
                                title={cmsNew("cms.new.auto.title.modifier", "Modifier")}
                                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                              >
                                <Pencil size={15} />
                              </button>
                            }
                          />
                          <button
                            type="button"
                            title={cmsNew("cms.new.auto.title.supprimer", "Supprimer")}
                            onClick={() => setDeleteFor(item)}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Suppression avec confirmation. */}
      <Dialog
        open={!!deleteFor}
        onOpenChange={(open) => {
          if (!open) setDeleteFor(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{cmsNew("cms.new.auto.text.supprimer_lactualite", "Supprimer l'actualité")}</DialogTitle>
            <DialogDescription>
              {cmsNew("cms.new.news.deleteConfirmBefore", "Cette action est définitive. «")}{" "}
              {deleteFor ? newsTitle(deleteFor, mainLang) : ""}{" "}
              {cmsNew("cms.new.news.deleteConfirmAfter", "» sera supprimée.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)}>
              {cmsPhrase(cmsNew, "cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteNews.isPending}
              onClick={confirmDelete}
            >
              {cmsPhrase(cmsNew, "delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CmsAdminShell>
  );
}
